import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { ENTITY_CARDS } from '../../../shared/entityCards';
import '../../../shared/ListRowHover.css';

// Entity Cards (issue #249, vis_relationship_lifecycle.md §4) — which
// cards an Entity's page and Add form show, per Relationship Type. Cards
// down the side, every active Relationship Type across the top, so a
// tenant's own Types get a column as soon as they're added. Each tick
// saves on its own.
const SYSTEM_ORDER = ['contact', 'prospect', 'customer', 'supplier'];
function columnOrder(a, b) {
  const ai = a.system_key ? SYSTEM_ORDER.indexOf(a.system_key) : SYSTEM_ORDER.length;
  const bi = b.system_key ? SYSTEM_ORDER.indexOf(b.system_key) : SYSTEM_ORDER.length;
  return ai - bi || a.name.localeCompare(b.name);
}

const headCellStyle = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' };
const cellStyle = { padding: '8px', borderTop: '1px solid #e3e7ee' };

function EntityCardsGrid() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const showError = useErrorToast();
  const dismissError = useDismissError();

  useEffect(() => {
    axios.get('/api/relationship-types', { params: { status: 'active' } })
      .then((res) => setTypes([...(res.data.data || [])].sort(columnOrder)))
      .catch((err) => showError(err, 'Could not load Relationship Types'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (type, cardKey, ticked) => {
    const cell = `${type.id}:${cardKey}`;
    setSaving(cell);
    try {
      const res = await axios.patch(`/api/relationship-types/${type.id}/cards`, { card_key: cardKey, ticked });
      setTypes((rows) => rows.map((t) => (t.id === type.id ? { ...t, card_keys: res.data.data.card_keys } : t)));
      dismissError();
    } catch (err) {
      showError(err, 'Could not save');
    }
    setSaving(null);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Entity Cards</h2>
      </div>
      <div style={{ margin: '8px 24px 0', fontSize: 13, color: '#5a6b85' }}>
        Which cards an Entity's page and Add form show. An Entity shows a card when any Relationship Type it holds has it ticked.
        Identity, Addresses, Phones, Emails and Activities always show.
      </div>

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflowX: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead className="list-head">
            <tr>
              <th style={{ ...headCellStyle, textAlign: 'left' }}>Card</th>
              {types.map((t) => (
                <th key={t.id} style={{ ...headCellStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>{t.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={types.length + 1} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa' }}>Loading...</td></tr>
            ) : ENTITY_CARDS.map((card) => (
              <tr key={card.key}>
                <td style={cellStyle}>
                  {card.label}
                  {card.withRecords && <span style={{ color: '#8a94a6', fontSize: 12 }}> (also shows when it has records)</span>}
                </td>
                {types.map((t) => {
                  const ticked = (t.card_keys || []).includes(card.key);
                  return (
                    <td key={t.id} style={{ ...cellStyle, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={ticked}
                        disabled={saving === `${t.id}:${card.key}`}
                        onChange={(e) => toggle(t, card.key, e.target.checked)}
                        aria-label={`${card.label} for ${t.name}`}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EntityCardsGrid;
